import axios from 'axios';
import { getApiUrl } from '../../../config/api';

export class EditUsuarioController {
  constructor() {
    this.usuario = {
      UsuarioId: '',
      Password: '',
      Nombre: '',
      Email: '',
      EsActivo: false,
      FechaConfirmacion: null,
      ZEmpleado: '',
      ZSerie: '',
      UsuarioIdConsulta: ''
    };
    this.originalPassword = '';
    this.fechaConfirmacionLocal = '';
    this.loading = false;
    this.saving = false;
    this.error = null;
  }

  /**
   * Inicializa el controlador con un ID de usuario
   * @param {string} usuarioId - ID del usuario a editar
   */
  async init(usuarioId) {
    if (usuarioId) {
      await this.fetchUsuario(usuarioId);
    }
  }

  /**
   * Obtiene los datos del usuario desde el servidor
   * @param {string} usuarioId - ID del usuario
   */
  async fetchUsuario(usuarioId) {
    this.loading = true;
    this.error = null;

    try {
      const response = await axios.post(getApiUrl('/api/Usuario/lectura'), 
        { UsuarioId: usuarioId }, 
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      this.usuario = response.data;
      this.originalPassword = this.usuario.Password;
      this.usuario.Password = ''; // No mostrar la contraseña actual por seguridad

      // Convertir la fecha a formato local para el input datetime-local
      if (this.usuario.FechaConfirmacion) {
        const fecha = new Date(this.usuario.FechaConfirmacion);
        this.fechaConfirmacionLocal = this.formatDateForInput(fecha);
      }

      this.loading = false;
    } catch (error) {
      console.error('Error al cargar usuario:', error);
      this.error = 'No se pudo cargar el usuario. Por favor, intente nuevamente.';
      this.loading = false;
      throw error;
    }
  }

  /**
   * Valida los datos del usuario antes de actualizar
   * @returns {boolean} true si los datos son válidos
   */
  validateUsuario() {
    if (!this.usuario.UsuarioId || this.usuario.UsuarioId.trim() === '') {
      this.error = 'El ID de usuario es requerido';
      return false;
    }

    if (this.usuario.Password && this.usuario.Password.length > 40) {
      this.error = 'La contraseña no puede exceder 40 caracteres';
      return false;
    }

    if (this.usuario.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.usuario.Email)) {
      this.error = 'El formato del email no es válido';
      return false;
    }

    if (this.usuario.Nombre && this.usuario.Nombre.length > 60) {
      this.error = 'El nombre no puede exceder 60 caracteres';
      return false;
    }

    if (this.usuario.Email && this.usuario.Email.length > 80) {
      this.error = 'El email no puede exceder 80 caracteres';
      return false;
    }

    if (this.usuario.ZEmpleado && this.usuario.ZEmpleado.length > 20) {
      this.error = 'ZEmpleado no puede exceder 20 caracteres';
      return false;
    }

    if (this.usuario.ZSerie && this.usuario.ZSerie.length > 20) {
      this.error = 'ZSerie no puede exceder 20 caracteres';
      return false;
    }

    if (this.usuario.UsuarioIdConsulta && this.usuario.UsuarioIdConsulta.length > 200) {
      this.error = 'Usuario ID Consulta no puede exceder 200 caracteres';
      return false;
    }

    this.error = null;
    return true;
  }

  /**
   * Prepara los datos del usuario antes del envío
   */
  prepareUsuarioData() {
    // Si no se ingresó una nueva contraseña, usar la original
    if (!this.usuario.Password) {
      this.usuario.Password = this.originalPassword;
    }

    // Actualizar la fecha de confirmación desde el input
    if (this.fechaConfirmacionLocal && this.usuario.EsActivo) {
      this.usuario.FechaConfirmacion = new Date(this.fechaConfirmacionLocal).toISOString();
    } else if (!this.usuario.EsActivo) {
      this.usuario.FechaConfirmacion = null;
    }
  }

  /**
   * Actualiza el usuario en el servidor
   * @returns {Promise} Promise que resuelve cuando el usuario es actualizado
   */
  async updateUsuario() {
    if (!this.validateUsuario()) {
      return Promise.reject(new Error(this.error));
    }

    this.saving = true;
    this.error = null;

    try {
      this.prepareUsuarioData();
      
      const response = await axios.post(getApiUrl('/api/Usuario/actualizar'), this.usuario);
      
      this.saving = false;
      return {
        success: true,
        message: response?.data?.message || 'Usuario actualizado correctamente.'
      };
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      this.error = 'No se pudo actualizar el usuario. Por favor, verifique los datos e intente nuevamente.';
      this.saving = false;
      throw error;
    }
  }

  /**
   * Maneja el cambio del estado activo del usuario
   */
  handleActiveChange() {
    if (this.usuario.EsActivo && !this.usuario.FechaConfirmacion) {
      // Si se activa el usuario y no tiene fecha de confirmación, establecer la fecha actual
      const now = new Date();
      this.fechaConfirmacionLocal = this.formatDateForInput(now);
    }
  }

  /**
   * Formatea una fecha para el input datetime-local
   * @param {Date} date - Fecha a formatear
   * @returns {string} Fecha formateada
   */
  formatDateForInput(date) {
    // Formato YYYY-MM-DDThh:mm requerido por input type="datetime-local"
    const pad = (num) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  /**
   * Actualiza un campo específico del usuario
   * @param {string} field - Nombre del campo
   * @param {any} value - Valor del campo
   */
  updateField(field, value) {
    if (Object.prototype.hasOwnProperty.call(this.usuario, field)) {
      this.usuario[field] = value;
      // Limpiar error cuando el usuario modifica los datos
      if (this.error) {
        this.error = null;
      }
    }
  }

  /**
   * Actualiza la fecha de confirmación local
   * @param {string} value - Valor de la fecha
   */
  updateFechaConfirmacion(value) {
    this.fechaConfirmacionLocal = value;
    if (this.error) {
      this.error = null;
    }
  }

  /**
   * Obtiene el estado actual del usuario
   * @returns {Object} Copia del objeto usuario
   */
  getUsuario() {
    return { ...this.usuario };
  }

  /**
   * Obtiene la fecha de confirmación local
   * @returns {string} Fecha de confirmación local
   */
  getFechaConfirmacionLocal() {
    return this.fechaConfirmacionLocal;
  }

  /**
   * Obtiene el estado de carga
   * @returns {boolean} true si está cargando
   */
  isLoading() {
    return this.loading;
  }

  /**
   * Obtiene el estado de guardado
   * @returns {boolean} true si está guardando
   */
  isSaving() {
    return this.saving;
  }

  /**
   * Obtiene el error actual
   * @returns {string|null} Mensaje de error o null
   */
  getError() {
    return this.error;
  }
}

// Exportar una instancia por defecto para uso directo
export default new EditUsuarioController();